/**
 * IntakeChecklist — TV-P2-05 + TV-P2-06
 *
 * Three patient-facing pre-visit steps. Each flips a single field in
 * the `intakeProgress` map on the patient's queue doc; the provider's
 * onSnapshot listener (TV-P2-02) sees the green check land in ~2s.
 *
 *  1. Consent       — scroll-through terms + "I agree" → consent=true
 *  2. Questionnaire — chief complaint + symptoms → questionnaire=true
 *                     (chiefComplaint also written back to the queue
 *                     doc so it appears on the provider's row card)
 *  3. A/V test      — getUserMedia probe; success → av=true. Friendly
 *                     remediation per browser error name.
 *
 * `useUpdateQueueDoc(patientId, tenantId)` is the only mutation
 * primitive — partial updates merge into the existing doc, no full
 * re-writes.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckCircle2,
  Circle,
  Loader2,
  Mic,
  Video,
  AlertTriangle,
} from 'lucide-react';
import { serverTimestamp } from 'firebase/firestore';

// ── Step component shells ──────────────────────────────────────────────────

function Step({ index, title, done, children }) {
  return (
    <div
      className={`p-4 rounded-lg border ${
        done ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-slate-800/50 border-slate-700'
      }`}
    >
      <div className="flex items-start gap-3 mb-3">
        {done ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        ) : (
          <Circle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
        )}
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Step {index}
          </p>
          <p className="text-sm font-semibold text-white">{title}</p>
        </div>
      </div>
      {!done && children}
    </div>
  );
}

// ── Step 1: Consent (TV-P4-03) ─────────────────────────────────────────────

function ConsentStep({
  done,
  onAgree,
  pending,
  existingConsent,
  isLoadingConsent,
  defaultName,
}) {
  const [scrolled, setScrolled] = useState(false);
  const [typedName, setTypedName] = useState(defaultName || '');
  const [agreed, setAgreed] = useState(false);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) setScrolled(true);
  };

  // When a Consent already exists, render an info card instead of the
  // capture flow — the parent has separately auto-flipped the queue
  // doc, so `done` will be true on next render. Brief loading state
  // covers the FHIR search round-trip.
  if (isLoadingConsent && !done) {
    return (
      <Step index={1} title="Consent for telehealth visit" done={false}>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Checking your consent on file…
        </div>
      </Step>
    );
  }
  if (existingConsent && !done) {
    return (
      <Step index={1} title="Consent for telehealth visit" done={false}>
        <p className="text-xs text-slate-400">
          A telehealth consent is already on file
          {existingConsent.dateTime ? (
            <> from {new Date(existingConsent.dateTime).toLocaleDateString()}</>
          ) : null}
          . Skipping capture.
        </p>
      </Step>
    );
  }

  // CAPTURE path — scroll terms → type name → check box → submit.
  // The submit handler POSTs a FHIR Consent (parent's recordConsent)
  // BEFORE flipping the queue-doc consent flag.
  const canSubmit =
    scrolled && agreed && typedName.trim().length >= 2 && !pending;

  return (
    <Step index={1} title="Consent for telehealth visit" done={done}>
      <div
        onScroll={onScroll}
        className="text-xs text-slate-400 leading-relaxed bg-slate-900 border border-slate-700 rounded p-3 h-24 overflow-y-auto"
      >
        <p>
          This telehealth visit is an alternative to in-person care. You
          consent to the use of audio and video for medical evaluation.
          Information shared is protected per CollabKare's privacy policy
          and applicable law. You may end the session at any time. Limits
          of remote care are similar to other video consultations:
          physical examination is not available; emergencies require in-
          person care or 911. Continue to scroll to enable consent.
        </p>
        <p className="mt-2">
          By typing your full legal name and clicking I agree, you confirm
          you have read and understood the above and consent to proceed
          with the visit. A signed Consent record will be stored in your
          chart.
        </p>
      </div>
      <div className="mt-3 space-y-2">
        <label className="block text-xs text-slate-400 font-medium">
          Type your full name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          placeholder="e.g. Jane Smith"
          disabled={!scrolled}
          className="w-full bg-slate-900 border border-slate-700 focus:border-blue-500 disabled:opacity-50 text-white text-sm rounded px-3 py-2 focus:outline-none"
        />
        <label className="flex items-start gap-2 text-xs text-slate-400 mt-2">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={!scrolled}
            className="mt-0.5 accent-blue-500 disabled:opacity-50"
          />
          <span>
            I confirm the name above is mine and I agree to the telehealth
            consent terms above.
          </span>
        </label>
      </div>
      <button
        onClick={() => onAgree({ typedName: typedName.trim() })}
        disabled={!canSubmit}
        className="mt-3 w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium py-2 rounded-lg flex items-center justify-center gap-2"
      >
        {pending && <Loader2 className="w-4 h-4 animate-spin" />}
        {scrolled
          ? agreed
            ? 'I agree — sign + start visit prep'
            : 'Confirm the checkbox to continue'
          : 'Scroll to the end to enable'}
      </button>
    </Step>
  );
}

// ── Step 2: Intake questionnaire ───────────────────────────────────────────

function QuestionnaireStep({ done, defaultChiefComplaint, onSubmit, pending }) {
  const [chiefComplaint, setChiefComplaint] = useState(defaultChiefComplaint || '');
  const [symptoms, setSymptoms] = useState('');
  const canSubmit = chiefComplaint.trim().length >= 3;
  return (
    <Step index={2} title="Tell us why you're here" done={done}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit || pending) return;
          onSubmit({ chiefComplaint: chiefComplaint.trim(), symptoms: symptoms.trim() });
        }}
        className="space-y-2"
      >
        <label className="block text-xs text-slate-400 font-medium">
          Chief complaint <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={chiefComplaint}
          onChange={(e) => setChiefComplaint(e.target.value)}
          placeholder="Briefly describe what's bothering you"
          className="w-full bg-slate-900 border border-slate-700 focus:border-blue-500 text-white text-sm rounded px-3 py-2 focus:outline-none"
        />
        <label className="block text-xs text-slate-400 font-medium pt-1">
          Anything else the provider should know? (optional)
        </label>
        <textarea
          value={symptoms}
          onChange={(e) => setSymptoms(e.target.value)}
          placeholder="Current symptoms, recent changes, etc."
          rows={3}
          className="w-full bg-slate-900 border border-slate-700 focus:border-blue-500 text-white text-sm rounded px-3 py-2 focus:outline-none resize-none"
        />
        <button
          type="submit"
          disabled={!canSubmit || pending}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium py-2 rounded-lg flex items-center justify-center gap-2 mt-1"
        >
          {pending && <Loader2 className="w-4 h-4 animate-spin" />}
          Save and continue
        </button>
      </form>
    </Step>
  );
}

// ── Step 3: A/V pre-test (TV-P2-06) ────────────────────────────────────────

const MEDIA_ERROR_HINTS = {
  NotAllowedError: {
    title: 'Camera + microphone permission denied',
    body: "We need access to your camera and microphone for the video visit. Click the camera icon in your browser's address bar and choose Allow, then click Test again.",
  },
  NotFoundError: {
    title: 'No camera or microphone detected',
    body: 'Plug in a webcam/headset (or check that your built-in one is connected), then click Test again.',
  },
  NotReadableError: {
    title: 'Camera or microphone is in use by another app',
    body: 'Close other video apps (Zoom, Teams, FaceTime, etc.) that might be holding the device, then click Test again.',
  },
  OverconstrainedError: {
    title: 'Your device settings don\'t match what we need',
    body: 'Try a different camera if you have one connected, or update your browser to the latest version.',
  },
  SecurityError: {
    title: 'Browser blocked the request for security reasons',
    body: 'Open this page in a secure (HTTPS) tab and try again.',
  },
  TypeError: {
    title: "This browser doesn't support video calls",
    body: 'Use a recent version of Chrome, Edge, Safari, or Firefox.',
  },
};

function AvTestStep({ done, onPass, pending }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [phase, setPhase] = useState('idle'); // idle | probing | ok | error
  const [errInfo, setErrInfo] = useState(null);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Stop tracks on unmount so we release the camera as soon as the
  // patient navigates away.
  useEffect(() => () => stop(), [stop]);

  const probe = async () => {
    setPhase('probing');
    setErrInfo(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setPhase('ok');
      // Tell the parent the pre-test passed; intakeProgress.av flips.
      try {
        await onPass();
      } catch (writeErr) {
        // Don't undo the local "ok" state if the doc write fails —
        // surface it as a soft warning while keeping the preview live.
        console.warn('[AvTest] av flag write failed:', writeErr);
      }
    } catch (err) {
      const name = err?.name || 'TypeError';
      setErrInfo(MEDIA_ERROR_HINTS[name] || {
        title: 'Camera + microphone test failed',
        body: err?.message || 'Unknown error. Try a different browser or check your device settings.',
      });
      setPhase('error');
    }
  };

  return (
    <Step index={3} title="Test your camera and microphone" done={done}>
      {phase === 'idle' && (
        <button
          onClick={probe}
          disabled={pending}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium py-2 rounded-lg flex items-center justify-center gap-2"
        >
          <Video className="w-4 h-4" />
          <Mic className="w-4 h-4" />
          Test camera and microphone
        </button>
      )}
      {phase === 'probing' && (
        <div className="flex items-center justify-center gap-2 text-slate-400 text-sm py-3">
          <Loader2 className="w-4 h-4 animate-spin" />
          Requesting permission…
        </div>
      )}
      {(phase === 'ok' || done) && (
        <div className="space-y-2">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full aspect-video bg-black rounded border border-slate-700"
          />
          <p className="text-xs text-emerald-400 text-center">
            Looks good — you're all set.
          </p>
        </div>
      )}
      {phase === 'error' && errInfo && (
        <div className="bg-red-500/10 border border-red-500/30 rounded p-3 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-200">{errInfo.title}</p>
              <p className="text-xs text-red-200/80 mt-1">{errInfo.body}</p>
            </div>
          </div>
          <button
            onClick={probe}
            className="w-full bg-red-600 hover:bg-red-700 text-white text-xs font-medium py-1.5 rounded"
          >
            Try again
          </button>
        </div>
      )}
    </Step>
  );
}

// ── Container ──────────────────────────────────────────────────────────────

/**
 * Wraps the three steps + drives Firestore writes via a single
 * `updateDoc` function injected from the parent (so the page owns the
 * Firestore client + the patientId/tenantId pair).
 *
 * Props:
 *   intakeProgress: { consent, questionnaire, av } from the queue doc
 *   chiefComplaint: current value on the queue doc (so the form
 *                   pre-fills if the patient came back)
 *   onPatch(partial): async (deepPartial) => Promise<void>
 */
export default function IntakeChecklist({
  intakeProgress,
  chiefComplaint,
  onPatch,
  // TV-P4-02/03 — Consent on file + FHIR write hook.
  existingConsent = null,
  isLoadingConsent = false,
  isRecordingConsent = false,
  recordConsent = null,
  consentDisplayName = '',
}) {
  const [pendingStep, setPendingStep] = useState(null);
  const [stepError, setStepError] = useState(null);
  const progress = intakeProgress || {};

  // Wraps each step's onPatch call so a Firestore failure surfaces in
  // the UI instead of dying silently in the console. The most common
  // failure is `permission-denied` from a stale Firebase session — the
  // banner copy spells out the recovery.
  const wrap = (key, fn) => async (...args) => {
    setPendingStep(key);
    setStepError(null);
    try {
      await fn(...args);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[IntakeChecklist] step '${key}' write failed:`, err);
      const isPerm =
        err?.code === 'permission-denied'
        || /permission/i.test(String(err?.message));
      setStepError({
        step: key,
        title: isPerm
          ? "Couldn't save — your session is out of date."
          : "Couldn't save just now.",
        body: isPerm
          ? 'Clear site data + reload this tab, or re-launch the visit from your patient portal. Your camera/mic test will run again on the new session.'
          : err?.message || 'Try again in a moment.',
      });
      // Don't rethrow — the banner is the user feedback. AvTestStep's
      // local catch becomes a no-op which is the intent.
    } finally {
      setPendingStep(null);
    }
  };

  const completeConsent = wrap('consent', async ({ typedName } = {}) => {
    // TV-P4-03: POST a FHIR Consent FIRST so the patient's chart has
    // a signed record. Skip the POST if a Consent already exists
    // (the parent does the auto-backfill) or if no recordConsent fn
    // is supplied (defensive — keeps the component usable in tests).
    if (recordConsent && !existingConsent && typedName) {
      await recordConsent({ typedName });
    }
    return onPatch({
      'intakeProgress.consent': true,
      consentSignedAt: serverTimestamp(),
    });
  });

  const submitQuestionnaire = wrap('questionnaire', ({ chiefComplaint: cc, symptoms }) =>
    onPatch({
      'intakeProgress.questionnaire': true,
      chiefComplaint: cc,
      intakeSymptoms: symptoms || null,
    }),
  );

  const passAvTest = wrap('av', () =>
    onPatch({
      'intakeProgress.av': true,
      avTestedAt: serverTimestamp(),
    }),
  );

  return (
    <div className="space-y-3">
      {stepError && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
          <p className="text-sm font-semibold text-red-200 mb-1">{stepError.title}</p>
          <p className="text-xs text-red-200/80">{stepError.body}</p>
        </div>
      )}
      <ConsentStep
        done={!!progress.consent}
        pending={pendingStep === 'consent' || isRecordingConsent}
        onAgree={completeConsent}
        existingConsent={existingConsent}
        isLoadingConsent={isLoadingConsent}
        defaultName={consentDisplayName}
      />
      <QuestionnaireStep
        done={!!progress.questionnaire}
        defaultChiefComplaint={chiefComplaint}
        pending={pendingStep === 'questionnaire'}
        onSubmit={submitQuestionnaire}
      />
      <AvTestStep
        done={!!progress.av}
        pending={pendingStep === 'av'}
        onPass={passAvTest}
      />
    </div>
  );
}
